import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  GoogleAuthProvider,
  signInWithRedirect,
  signInWithPopup,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { Shield, CheckCircle2, AlertCircle } from "lucide-react";
import { auth as firebaseAuth } from "../../lib/firebase";
import { Auth } from "../../lib/apiTyped";
import { ApiError } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";
import { Spinner } from "../../components/shared/Spinner";

type Preview = {
  email: string;
  kind: "org_owner" | "member";
  orgName: string | null;
  expiresAt: string | null;
};

export function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const { user, refreshClaims } = useAuth();

  const [preview, setPreview] = useState<Preview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Two flags so the auto-redeem effect is never blocked by the sign-in
  // button's own pending state. UI shows "Procesando…" if either is true.
  const [signingIn, setSigningIn] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const busy = signingIn || redeeming;

  // 1. Load invite preview from public endpoint.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setPreviewError("Falta el token de la invitación.");
      setLoadingPreview(false);
      return;
    }
    Auth.invitePreview(token)
      .then((p) => {
        if (!cancelled) {
          setPreview(p);
          setLoadingPreview(false);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setPreviewError(
          e instanceof ApiError ? e.message : "No se pudo cargar la invitación.",
        );
        setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // 2. Auto-redeem once Firebase Auth has populated `user` and the email
  //    matches the invite. The displayName is captured later in the in-app
  //    onboarding (StepName); here we send an empty placeholder and the
  //    backend falls back to the email-prefix.
  useEffect(() => {
    if (!user || !preview || success || redeeming) return;
    const userEmail = (user.email ?? "").toLowerCase();
    if (userEmail !== preview.email) return; // wrong email — render mismatch UI
    setRedeeming(true);
    setActionError(null);
    Auth.initialize(token)
      .then(async () => {
        await refreshClaims();
        setSuccess(true);
        setTimeout(() => navigate("/", { replace: true }), 800);
      })
      .catch((e) => {
        setActionError(
          e instanceof ApiError ? e.message : "No se pudo aceptar la invitación.",
        );
      })
      .finally(() => setRedeeming(false));
  }, [user, preview, success, redeeming, token, navigate, refreshClaims]);

  async function handleSignIn() {
    if (!firebaseAuth) return;
    setActionError(null);
    setSigningIn(true);
    const provider = new GoogleAuthProvider();
    if (preview?.email) provider.setCustomParameters({ login_hint: preview.email });
    try {
      // Popup resolves → onAuthStateChanged populates `user` → effect (2)
      // takes over and redeems the token. We only release `signingIn`
      // when the popup is fully done so the button stays "Procesando…"
      // through the whole handshake.
      await signInWithPopup(firebaseAuth, provider);
      setSigningIn(false);
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (
        code === "auth/popup-blocked" ||
        code === "auth/operation-not-supported-in-this-environment"
      ) {
        try {
          await signInWithRedirect(firebaseAuth, provider);
          return; // page navigates away; leave signingIn true
        } catch (e) {
          setActionError(e instanceof Error ? e.message : "Error al iniciar sesión.");
        }
      } else if (code !== "auth/popup-closed-by-user") {
        setActionError(err instanceof Error ? err.message : "Error al iniciar sesión.");
      }
      setSigningIn(false);
    }
  }

  async function handleSwitchAccount() {
    if (!firebaseAuth) return;
    await firebaseSignOut(firebaseAuth);
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-surface border border-border rounded-2xl p-8 shadow-card">
        {loadingPreview ? (
          <div className="flex flex-col items-center gap-3 py-6">
            <Spinner />
            <p className="text-sm text-ink-2">Cargando invitación…</p>
          </div>
        ) : previewError ? (
          <Centered icon={<AlertCircle className="w-10 h-10 text-error" />}>
            <h1 className="text-2xl font-black text-ink mb-3">
              Invitación no válida
            </h1>
            <p className="text-sm text-ink-2">{previewError}</p>
          </Centered>
        ) : success ? (
          <Centered
            icon={<CheckCircle2 className="w-10 h-10 text-emerald-500" />}
          >
            <h1 className="text-2xl font-black text-ink mb-3">
              Cuenta activada
            </h1>
            <p className="text-sm text-ink-2">Te llevamos a tu onboarding…</p>
          </Centered>
        ) : preview && user && user.email?.toLowerCase() !== preview.email ? (
          <Centered icon={<AlertCircle className="w-10 h-10 text-error" />}>
            <h1 className="text-2xl font-black text-ink mb-3">
              Cuenta incorrecta
            </h1>
            <p className="text-sm text-ink-2 mb-6">
              Has iniciado sesión como <strong>{user.email}</strong>, pero la
              invitación es para <strong>{preview.email}</strong>.
            </p>
            <button
              onClick={handleSwitchAccount}
              className="bg-ink text-white text-sm font-medium px-5 py-2 rounded-full hover:opacity-80 transition-opacity"
            >
              Cerrar sesión
            </button>
          </Centered>
        ) : (
          <Centered icon={<Shield className="w-10 h-10 text-ink-3" />}>
            <h1 className="text-2xl font-black text-ink mb-2">
              {preview?.kind === "org_owner"
                ? "Activa tu cuenta de Cowboy"
                : `Únete a ${preview?.orgName ?? "tu equipo"}`}
            </h1>
            <p className="text-sm text-ink-2 mb-6">
              Inicia sesión con Google con <strong>{preview?.email}</strong>{" "}
              para aceptar la invitación.
            </p>
            <button
              onClick={handleSignIn}
              disabled={busy}
              className="bg-ink text-white text-sm font-medium px-5 py-2 rounded-full hover:opacity-80 transition-opacity disabled:opacity-50"
            >
              {busy ? "Procesando…" : "Continuar con Google"}
            </button>
            {actionError && (
              <p className="mt-4 text-sm text-error">{actionError}</p>
            )}
          </Centered>
        )}
      </div>
    </div>
  );
}

function Centered({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="text-center">
      <div className="flex justify-center mb-4">{icon}</div>
      {children}
    </div>
  );
}
